<template>
  <div class="staff-form-container">
    <div class="page-header">
      <div class="breadcrumb">ນັກສຶກສາຮຽນຕ່າງປະເທດ / <span>{{ isEdit ? 'ແກ້ໄຂຂໍ້ມູນ' : 'ລົງທະບຽນນັກສຶກສາໃໝ່' }}</span></div>
      <div class="header-content">
        <h1>{{ isEdit ? 'ແກ້ໄຂຂໍ້ມູນນັກສຶກສາຮຽນຕ່າງປະເທດ' : 'ລົງທະບຽນນັກສຶກສາຮຽນຕ່າງປະເທດໃໝ່' }}</h1>
      </div>
    </div>

    <form @submit.prevent="handleSubmit">
      <div class="form-layout">
        <!-- ສ່ວນເບື້ອງຊ້າຍ: ຮູບພາບ & ລະຫັດ -->
        <div class="form-sidebar">
          <div class="panel avatar-panel">
            <div class="avatar-wrapper">
              <div class="avatar-view">
                <img :src="form.current_photo_base64 || require('@/assets/logo.png')" :class="{ 'is-empty': !form.current_photo_base64 }">
                <button v-if="form.current_photo_base64" type="button" class="remove-avatar" @click="removePhoto">×</button>
              </div>
              <label class="upload-label">
                <i class="fas fa-camera"></i> ອັບໂຫຼດຮູບ
                <input type="file" @change="handlePhotoUpload" accept="image/*" hidden>
              </label>
            </div>

            <div class="id-section">
              <div class="form-group" :class="{ 'has-error': errors.student_code }">
                <label>ລະຫັດນັກສຶກສາ <span class="req">*</span></label>
                <div class="id-input-group">
                  <input type="text" v-model="form.student_code" readonly placeholder="Auto ID">
                  <button v-if="!isEdit" type="button" @click="generateStudentCode" class="refresh-id" title="ສ້າງລະຫັດໃໝ່">
                    <i class="fas fa-magic"></i> <span>ສ້າງລະຫັດ</span>
                  </button>
                </div>
                <div class="error-text" v-if="errors.student_code">{{ errors.student_code }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ສ່ວນເບື້ອງຂວາ: Tabs -->
        <div class="form-main">
          <div class="tabs-container">
            <div class="tabs-nav">
              <button type="button" class="tab-btn" :class="{ active: activeTab === 'personal' }" @click="activeTab = 'personal'">
                <i class="fas fa-id-card"></i> ຂໍ້ມູນສ່ວນຕົວ ແລະ ພື້ນຖານ
                <i v-if="tabStatus.personal" class="fas fa-check-circle tab-complete-icon"></i>
              </button>
              <button type="button" class="tab-btn" :class="{ active: activeTab === 'academic' }" @click="activeTab = 'academic'">
                <i class="fas fa-graduation-cap"></i> ລາຍລະອຽດຫຼັກສູດ
                <i v-if="tabStatus.academic" class="fas fa-check-circle tab-complete-icon"></i>
              </button>
              <button type="button" class="tab-btn" :class="{ active: activeTab === 'funding' }" @click="activeTab = 'funding'">
                <i class="fas fa-hand-holding-usd"></i> ທຶນການສຶກສາ
                <i v-if="tabStatus.funding" class="fas fa-check-circle tab-complete-icon"></i>
              </button>
            </div>

            <div class="tab-content border-top pt-4">
              <!-- Tab 1: ຂໍ້ມູນສ່ວນຕົວ ແລະ ພື້ນຖານ (Part1) -->
              <div v-if="activeTab === 'personal'" class="tab-pane">
                <div class="field-row">
                  <div class="form-group" :class="{ 'has-error': errors.first_name_lao }">
                    <label>ຊື່ (ພາສາລາວ) <span class="req">*</span></label>
                    <input type="text" v-model="form.first_name_lao" @input="clearError('first_name_lao')" placeholder="ຊື່">
                    <div class="error-text" v-if="errors.first_name_lao">{{ errors.first_name_lao }}</div>
                  </div>
                  <div class="form-group" :class="{ 'has-error': errors.last_name_lao }">
                    <label>ນາມສະກຸນ (ພາສາລາວ) <span class="req">*</span></label>
                    <input type="text" v-model="form.last_name_lao" @input="clearError('last_name_lao')" placeholder="ນາມສະກຸນ">
                    <div class="error-text" v-if="errors.last_name_lao">{{ errors.last_name_lao }}</div>
                  </div>
                </div>
                <div class="field-row">
                  <div class="form-group">
                    <label>ຊື່ (ພາສາອັງກິດ)</label>
                    <input type="text" v-model="form.first_name_eng" placeholder="First name as in passport">
                  </div>
                  <div class="form-group">
                    <label>ນາມສະກຸນ (ພາສາອັງກິດ)</label>
                    <input type="text" v-model="form.last_name_eng" placeholder="Last name as in passport">
                  </div>
                </div>
                <div class="field-row-3">
                  <div class="form-group" :class="{ 'has-error': errors.gender }">
                    <label>ເພດ <span class="req">*</span></label>
                    <select v-model="form.gender" @change="clearError('gender')">
                      <option value="ຊ">ຊາຍ</option>
                      <option value="ຍ">ຍິງ</option>
                      <option value="ອື່ນໆ">ອື່ນໆ</option>
                    </select>
                    <div class="error-text" v-if="errors.gender">{{ errors.gender }}</div>
                  </div>
                  <div class="form-group" :class="{ 'has-error': errors.date_of_birth }">
                    <label>ວັນເດືອນປີເກີດ <span class="req">*</span></label>
                    <input type="text" v-model="dobText" @input="handleDobInput" placeholder="dd/mm/yyyy" inputmode="numeric" maxlength="10">
                    <div class="error-text" v-if="errors.date_of_birth">{{ errors.date_of_birth }}</div>
                  </div>
                  <div class="form-group" :class="{ 'has-error': errors.passport_number }">
                    <label>ເລກໜັງສືຜ່ານແດນ <span class="req">*</span></label>
                    <input type="text" v-model="form.passport_number" @input="clearError('passport_number')" placeholder="Passport No.">
                    <div class="error-text" v-if="errors.passport_number">{{ errors.passport_number }}</div>
                  </div>
                </div>

                <div class="record-card mb-4 mt-4">
                  <div class="record-header"><h4><i class="fas fa-map-marked-alt"></i> ທີ່ຢູ່ບ້ານເກີດ</h4></div>
                  <div class="field-row-4">
                    <div class="form-group">
                      <label>ແຂວງເກີດ</label>
                      <div class="input-with-action pointer" @click="openLocationModal('province')">
                        <input type="text" :value="locationNames.home_province" readonly placeholder="ເລືອກແຂວງ">
                        <button type="button" class="btn-icon"><i class="fas fa-map-marker-alt"></i></button>
                      </div>
                    </div>
                    <div class="form-group">
                      <label>ເມືອງເກີດ</label>
                      <div class="input-with-action pointer" :class="{ 'disabled': !form.home_prov_id }" @click="openLocationModal('district')">
                        <input type="text" :value="locationNames.home_district" readonly placeholder="ເລືອກເມືອງ">
                        <button type="button" class="btn-icon"><i class="fas fa-search"></i></button>
                      </div>
                    </div>
                    <div class="form-group"><label>ຕາແສງເກີດ</label><input type="text" v-model="form.home_commune" placeholder="ຕາແສງ"></div>
                    <div class="form-group"><label>ບ້ານເກີດ</label><input type="text" v-model="form.home_village" placeholder="ບ້ານ"></div>
                  </div>
                </div>

                <div class="record-card mb-4">
                  <div class="record-header"><h4><i class="fas fa-phone-alt"></i> ຜູ້ຕິດຕໍ່ສຸກເສີນ ຢູ່ບ້ານເກີດ</h4></div>
                  <div class="field-row-4">
                    <div class="form-group"><label>ຊື່ຜູ້ຕິດຕໍ່</label><input type="text" v-model="form.home_emergency_contact_name" placeholder="ຊື່ ນາມສະກຸນ"></div>
                    <div class="form-group">
                      <label>ສາຍພົວພັນ</label>
                      <select v-model="form.home_emergency_contact_relation">
                        <option value="">-- ເລືອກສາຍພົວພັນ --</option>
                        <option v-for="r in relationOptions" :key="r" :value="r">{{ r }}</option>
                      </select>
                    </div>
                    <div class="form-group"><label>ເບີໂທ</label><input type="text" v-model="form.home_emergency_contact_number" placeholder="20XXXXXXXX"></div>
                    <div class="form-group"><label>ອີເມວ</label><input type="email" v-model="form.home_emergency_contact_email" placeholder="name@email.com"></div>
                  </div>
                  <div class="field-row">
                    <div class="form-group"><label>ຂໍ້ມູນອື່ນໆ</label><textarea v-model="form.home_emergency_contact_note" rows="2" placeholder="ຂໍ້ມູນເພີ່ມເຕີມກ່ຽວກັບຜູ້ຕິດຕໍ່ສຸກເສີນ"></textarea></div>
                  </div>
                </div>

                <div class="record-card">
                  <div class="record-header"><h4><i class="fas fa-house-user"></i> ຂໍ້ມູນຕິດຕໍ່ປັດຈຸບັນ</h4></div>
                  <div class="field-row">
                    <div class="form-group"><label>ເບີຕິດຕໍ່ຢູ່ລາວ</label><input type="text" v-model="form.current_home_number" placeholder="ເບີໂທຢູ່ລາວ"></div>
                    <div class="form-group"><label>ເບີຕິດຕໍ່ຕ່າງປະເທດ</label><input type="text" v-model="form.current_contact_number" placeholder="ເບີໂທຕ່າງປະເທດ"></div>
                  </div>
                  <div class="field-row">
                    <div class="form-group"><label>ອີເມວຕິດຕໍ່ຕ່າງປະເທດ</label><input type="email" v-model="form.current_contact_email" placeholder="name@email.com"></div>
                    <div class="form-group"><label>ຊ່ອງທາງຕິດຕໍ່ອື່ນໆ</label><input type="text" v-model="form.current_other_contact" placeholder="WhatsApp / Line / V-Chat"></div>
                  </div>
                  <div class="field-row">
                    <div class="form-group"><label>ທີ່ຢູ່ປັດຈຸບັນ (ພາສາອັງກິດ)</label><textarea v-model="form.current_address_eng" rows="2"></textarea></div>
                    <div class="form-group"><label>ທີ່ຢູ່ປັດຈຸບັນ (ພາສາທ້ອງຖິ່ນ)</label><textarea v-model="form.current_address_local" rows="2"></textarea></div>
                  </div>
                </div>
              </div>

              <!-- Tab 2: ລາຍລະອຽດຫຼັກສູດ (Part2) -->
              <div v-if="activeTab === 'academic'" class="tab-pane">
                <div class="field-row">
                  <div class="form-group" :class="{ 'has-error': errors.host_country }">
                    <label>ປະເທດທີ່ໄປສຶກສາ <span class="req">*</span></label>
                    <select v-model="form.host_country" @change="clearError('host_country')" :disabled="lockHostCountry">
                      <option value="">-- ເລືອກປະເທດ --</option>
                      <option v-for="c in hostCountryOptions" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <div class="field-hint" v-if="lockHostCountry">
                      <i class="fas fa-lock"></i> ບັນຊີກົງສຸນສາມາດບັນທຶກໄດ້ສະເພາະນັກສຶກສາໃນປະເທດທີ່ຮັບຜິດຊອບເທົ່ານັ້ນ
                    </div>
                    <div class="error-text" v-if="errors.host_country">{{ errors.host_country }}</div>
                  </div>
                  <div class="form-group" :class="{ 'has-error': errors.degree_level }">
                    <label>ລະດັບການສຶກສາ <span class="req">*</span></label>
                    <select v-model="form.degree_level" @change="clearError('degree_level')">
                      <option :value="null">-- ເລືອກລະດັບ --</option>
                      <option v-for="d in degreeLevelOptions" :key="d" :value="d">{{ d }}</option>
                    </select>
                    <div class="error-text" v-if="errors.degree_level">{{ errors.degree_level }}</div>
                  </div>
                </div>

                <div class="field-row">
                  <div class="form-group"><label>ຊື່ສະຖາບັນການສຶກສາ / ມະຫາວິທະຍາໄລ</label><input type="text" v-model="form.university_name" placeholder="ຊື່ສະຖາບັນ"></div>
                  <div class="form-group"><label>ຊື່ສະຖາບັນ (ພາສາອັງກິດ)</label><input type="text" v-model="form.university_name_eng" placeholder="Official name"></div>
                </div>
                <div class="field-row">
                  <div class="form-group"><label>ຊື່ສະຖາບັນ (ພາສາທ້ອງຖິ່ນ)</label><input type="text" v-model="form.university_name_local" placeholder="Local name"></div>
                  <div class="form-group"><label>ສະຖານທີ່ຮຽນ (Campus)</label><input type="text" v-model="form.campus_name" placeholder="ເຊັ່ນ: Parkville Campus"></div>
                </div>

                <div class="field-row">
                  <div class="form-group" :class="{ 'has-error': errors.field_of_study }">
                    <label>ສາຂາວິຊາສະເພາະ <span class="req">*</span></label>
                    <select v-model="form.field_of_study" @change="clearError('field_of_study')">
                      <option :value="null">-- ເລືອກສາຂາ --</option>
                      <option v-for="f in fieldOfStudyOptions" :key="f" :value="f">{{ f }}</option>
                    </select>
                    <div class="error-text" v-if="errors.field_of_study">{{ errors.field_of_study }}</div>
                  </div>
                  <div class="form-group"><label>ຊື່ຫຼັກສູດທີ່ຮຽນ</label><input type="text" v-model="form.course_name" placeholder="Bachelor of Art, Master of Art..."></div>
                </div>

                <div class="field-row">
                  <div class="form-group"><label>ຄະນະທີ່ຮຽນ</label><input type="text" v-model="form.department_school_name" placeholder="Department of Medicine, School of Art"></div>
                  <div class="form-group"><label>ວິຊາສະເພາະເຈາະຈົງ</label><input type="text" v-model="form.major_detail" placeholder="ເຊັ່ນ: Civil Engineering"></div>
                </div>

                <div class="field-row">
                  <div class="form-group"><label>ເບີໂທຕິດຕໍ່ຄະນະທີ່ຮຽນ</label><input type="text" v-model="form.department_contact_number" placeholder="ເບີໂທຕິດຕໍ່ຄະນະ/ພາກວິຊາ"></div>
                  <div class="form-group"><label>ອີເມວຕິດຕໍ່ຄະນະທີ່ຮຽນ</label><input type="email" v-model="form.department_contact_email" placeholder="department@university.edu"></div>
                </div>

                <div class="field-row">
                  <div class="form-group" :class="{ 'has-error': errors.academic_year_start }">
                    <label>ສົກຮຽນເລີ່ມຕົ້ນ <span class="req">*</span></label>
                    <input type="text" v-model="form.academic_year_start" @input="handleAcademicYearStartInput" placeholder="ຮູບແບບ: 2026-2027" inputmode="numeric" maxlength="9">
                    <div class="error-text" v-if="errors.academic_year_start">{{ errors.academic_year_start }}</div>
                  </div>
                  <div class="form-group" :class="{ 'has-error': errors.expected_graduation }">
                    <label>ປີຄາດວ່າຈະຈົບການສຶກສາ / ປີທີ່ຈົບ <span class="req">*</span></label>
                    <input type="text" v-model="form.expected_graduation" @input="handleExpectedGraduationInput" placeholder="ຮູບແບບ: 2030" inputmode="numeric" maxlength="4">
                    <div class="error-text" v-if="errors.expected_graduation">{{ errors.expected_graduation }}</div>
                  </div>
                </div>
                <div class="field-row">
                  <div class="form-group">
                    <label>ສະຖານະການສຶກສາ</label>
                    <select v-model="form.student_status">
                      <option v-for="s in studentStatusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Tab 3: ທຶນການສຶກສາ (Part3) -->
              <div v-if="activeTab === 'funding'" class="tab-pane">
                <div class="field-row">
                  <div class="form-group" :class="{ 'has-error': errors.funding_type }">
                    <label>ປະເພດທຶນການສຶກສາ <span class="req">*</span></label>
                    <select v-model="form.funding_type" @change="clearError('funding_type')">
                      <option :value="null">-- ເລືອກປະເພດທຶນ --</option>
                      <option v-for="f in fundingTypeOptions" :key="f" :value="f">{{ f }}</option>
                    </select>
                    <div class="error-text" v-if="errors.funding_type">{{ errors.funding_type }}</div>
                  </div>
                  <div class="form-group"><label>ລະອຽດຜູ້ສະໜັບສະໜູນ</label><input type="text" v-model="form.sponsor_details" placeholder="ຊື່ໂຄງການ, ກະຊວງ ຫຼື ບໍລິສັດ"></div>
                </div>

                <div class="record-card">
                  <div class="record-header"><h4><i class="fas fa-file-upload"></i> ອັບໂຫລດຂໍ້ມູນກ່ຽວກັບທຶນ (MoU / ໃບຢັ້ງຢືນຈາກ MOES)</h4></div>
                  <label class="upload-label file-upload-label">
                    <i class="fas fa-paperclip"></i> ເລືອກເອກະສານ (ຫຼາຍໄຟລ໌)
                    <input type="file" multiple @change="handleMultiFileUpload($event, 'moes_agreement_files')" hidden>
                  </label>
                  <ul class="file-list" v-if="form.moes_agreement_files.length">
                    <li v-for="(f, idx) in form.moes_agreement_files" :key="idx">
                      <i class="fas fa-file-alt"></i> {{ f.name }}
                      <button type="button" class="btn-remove-file" @click="removeMultiFile('moes_agreement_files', idx)">×</button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="form-actions-wrapper mt-4">
            <button type="button" class="btn-cancel" @click="$router.back()">ຍົກເລີກ</button>
            <button type="submit" class="btn-save" :disabled="submitting">
              <i class="fas fa-save"></i> {{ submitting ? 'ກຳລັງບັນທຶກ...' : (isEdit ? 'ອັບເດດຂໍ້ມູນ' : 'ບັນທຶກຂໍ້ມູນນັກສຶກສາ') }}
            </button>
          </div>
        </div>
      </div>
    </form>

    <!-- Modal ແຈ້ງຜົນຮັບ -->
    <transition name="modal">
      <div v-if="resultModal.show" class="modal-overlay" @mousedown.self="closeResultModal">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon" :class="resultModal.type">
            <i class="fas" :class="resultModal.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'"></i>
          </div>
          <div class="modal-content-area">
            <h2>{{ resultModal.title }}</h2>
            <p>{{ resultModal.message }}</p>
          </div>
          <div class="modal-footer-actions">
            <button class="btn-modal-primary" :class="resultModal.type" @click="closeResultModal">ເຂົ້າໃຈແລ້ວ</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal ເລືອກສະຖານທີ່ -->
    <transition name="modal">
      <div v-if="locationModal.show" class="modal-overlay" @mousedown.self="closeLocationModal">
        <div class="custom-modal-card" role="dialog" aria-modal="true">
          <div class="custom-modal-header">
            <h3>{{ locationModal.title }}</h3>
            <button @click="closeLocationModal" class="close-btn">&times;</button>
          </div>
          <div class="custom-modal-search">
            <div class="input-container">
              <i class="fas fa-search search-icon"></i>
              <input type="text" v-model="locationModal.searchQuery" placeholder="ຄົ້ນຫາ..." ref="locationSearchInput">
            </div>
          </div>
          <div class="custom-modal-body">
            <div v-if="locationModal.loading" class="loading-state"><i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດ...</div>
            <template v-else>
              <div v-for="item in filteredLocationItems" :key="item.id" class="list-item" @click="selectLocationItem(item)">
                <span class="item-name">{{ item.name }}</span>
                <i class="fas fa-chevron-right"></i>
              </div>
              <div v-if="filteredLocationItems.length === 0" class="empty-state">ບໍ່ພົບຂໍ້ມູນ</div>
            </template>
          </div>
          <div class="custom-modal-footer">
            <button type="button" class="btn-modal-cancel" @click="closeLocationModal">ຍົກເລີກ</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';
import { COUNTRIES } from '@/utils/countries';

export default {
  name: 'OverseaStudentFormPage',
  data() {
    return {
      isEdit: false,
      dobText: '',
      submitting: false,
      activeTab: 'personal',
      user: {},
      form: {
        student_code: '',
        first_name_lao: '',
        last_name_lao: '',
        first_name_eng: '',
        last_name_eng: '',
        gender: 'ຊ',
        date_of_birth: '',
        passport_number: '',
        home_prov_id: null,
        home_district_id: null,
        home_commune: '',
        home_village: '',
        home_emergency_contact_name: '',
        home_emergency_contact_relation: '',
        home_emergency_contact_number: '',
        home_emergency_contact_email: '',
        home_emergency_contact_note: '',
        current_home_number: '',
        current_contact_number: '',
        current_contact_email: '',
        current_other_contact: '',
        current_address_eng: '',
        current_address_local: '',
        host_country: '',
        university_name: '',
        university_name_eng: '',
        university_name_local: '',
        degree_level: null,
        field_of_study: null,
        course_name: '',
        department_school_name: '',
        department_contact_number: '',
        department_contact_email: '',
        campus_name: '',
        major_detail: '',
        academic_year_start: '',
        expected_graduation: '',
        current_photo_base64: null,
        funding_type: null,
        sponsor_details: '',
        moes_agreement_files: [],
        student_status: 'Active',
        verification_status: 'Pending Review',
        supporting_docs: [],
        comments: ''
      },
      hostCountryOptions: COUNTRIES,
      relationOptions: ['ພໍ່', 'ແມ່', 'ອ້າຍ', 'ເອື້ອຍ', 'ນ້ອງສາວ', 'ນ້ອງຊາຍ', 'ຍາດພີ່ນ້ອງ', 'ໝູ່', 'ເຈົ້າອາວາດ','ອື່ນໆ'],
      degreeLevelOptions: ['Bachelor', 'Master', 'PhD', 'Short-term Technical Training', 'TAFE', 'Others'],
      fieldOfStudyOptions: [],
      studentStatusOptions: [
        { value: 'Active', label: 'ກຳລັງສຶກສາ' },
        { value: 'Graduated', label: 'ຮຽນຈົບແລ້ວ' },
        { value: 'Withdrawn', label: 'ຍົກເລີກການສຶກສາ' },
        { value: 'Temporarily', label: 'ໂຈະຊົ່ວຄ່າວ' }
      ],
      fundingTypeOptions: [],
      errors: {},
      resultModal: { show: false, type: 'success', title: '', message: '' },
      locationModal: { show: false, title: '', type: '', loading: false, searchQuery: '', items: [] },
      locationNames: { home_province: '', home_district: '' }
    };
  },
  computed: {
    lockHostCountry() {
      return this.user.org_role === 'consulate' && !!this.user.country;
    },
    fullNameLao() {
      return [this.form.first_name_lao, this.form.last_name_lao].filter(Boolean).join(' ');
    },
    fullNameEng() {
      return [this.form.first_name_eng, this.form.last_name_eng].filter(Boolean).join(' ');
    },
    tabStatus() {
      return {
        personal: !!(this.form.first_name_lao && this.form.last_name_lao && this.form.gender && this.form.date_of_birth && this.form.passport_number),
        academic: !!(this.form.host_country && this.form.degree_level && this.form.field_of_study),
        funding: !!this.form.funding_type
      };
    },
    filteredLocationItems() {
      if (!this.locationModal.searchQuery) return this.locationModal.items;
      const q = this.locationModal.searchQuery.toLowerCase();
      return this.locationModal.items.filter(i => i.name.toLowerCase().includes(q));
    }
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem('user-info') || '{}');
    this.fetchFieldOfStudyOptions();
    this.fetchFundingTypeOptions();
    const id = this.$route.params.id;
    if (id) {
      this.isEdit = true;
      this.fetchData(id);
      document.addEventListener('keydown', this.handleKeyDown);
    } else {
      if (this.lockHostCountry) this.form.host_country = this.user.country;
      this.generateStudentCode();
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.enableScroll();
  },
  methods: {
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.locationModal.show) this.closeLocationModal();
    },
    disableScroll() { document.body.style.overflow = 'hidden'; },
    enableScroll() { document.body.style.overflow = ''; },
    clearError(field) {
      if (this.errors[field]) this.$set(this.errors, field, null);
    },
    async fetchFieldOfStudyOptions() {
      try {
        const res = await api.get('/getItems/field_of_study_options/name_lao/name_lao');
        this.fieldOfStudyOptions = (res.data || []).map(i => i.text);
      } catch (e) {
        console.error('fetchFieldOfStudyOptions error:', e);
      }
    },
    async fetchFundingTypeOptions() {
      try {
        const res = await api.get('/getItems/funding_type_options/name_lao/name_lao');
        this.fundingTypeOptions = (res.data || []).map(i => i.text);
      } catch (e) {
        console.error('fetchFundingTypeOptions error:', e);
      }
    },
    formatDobForDisplay(iso) {
      if (!iso) return '';
      const [y, m, d] = String(iso).split('-');
      if (!y || !m || !d) return '';
      return `${d}/${m}/${y}`;
    },
    handleDobInput() {
      const digits = this.dobText.replace(/\D/g, '').slice(0, 8);
      let formatted = digits;
      if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
      else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
      this.dobText = formatted;

      if (digits.length === 8) {
        const day = digits.slice(0, 2), month = digits.slice(2, 4), year = digits.slice(4, 8);
        this.form.date_of_birth = `${year}-${month}-${day}`;
        this.clearError('date_of_birth');
      } else {
        this.form.date_of_birth = '';
      }
    },
    async generateStudentCode() {
      try {
        const year = new Date().getFullYear();
        const prefix = `OS-${year}-`;
        const res = await api.get('/oversea-students', { params: { limit: 1, sort: '-student_code', student_code_prefix: prefix } });
        let nextSerial = 1;
        if (res.data.code === 1 && res.data.rows && res.data.rows.length > 0) {
          const match = res.data.rows[0].student_code.match(/-(\d{6})$/);
          if (match) nextSerial = parseInt(match[1], 10) + 1;
        }
        this.form.student_code = `${prefix}${String(nextSerial).padStart(6, '0')}`;
        this.clearError('student_code');
      } catch (e) {
        console.error('Gen code error:', e);
      }
    },
    async fetchData(id) {
      try {
        const res = await api.get(`/get/oversea_students/${id}`);
        if (res.data.code === 1 && res.data.rows) {
          if (this.lockHostCountry && res.data.rows.host_country !== this.user.country) {
            this.$router.replace('/forbidden');
            return;
          }
          this.form = { ...this.form, ...res.data.rows };
          if (!this.form.first_name_lao && this.form.full_name_lao) {
            const parts = this.form.full_name_lao.trim().split(/\s+/);
            this.form.first_name_lao = parts[0] || '';
            this.form.last_name_lao = parts.slice(1).join(' ');
          }
          if (!this.form.first_name_eng && this.form.full_name_eng) {
            const parts = this.form.full_name_eng.trim().split(/\s+/);
            this.form.first_name_eng = parts[0] || '';
            this.form.last_name_eng = parts.slice(1).join(' ');
          }
          this.dobText = this.formatDobForDisplay(this.form.date_of_birth);
          this.populateLocationNames();
        } else {
          this.showResult('error', 'ບໍ່ພົບຂໍ້ມູນ', 'ບໍ່ສາມາດໂຫຼດຂໍ້ມູນນັກສຶກສາໄດ້');
        }
      } catch (e) {
        console.error('fetchData error:', e);
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີໄດ້');
      }
    },
    async populateLocationNames() {
      try {
        if (this.form.home_prov_id) {
          const prov = await api.get('/getItems/province/prov_name/prov_id');
          const found = (prov.data || []).find(i => i.value == this.form.home_prov_id);
          if (found) this.locationNames.home_province = found.text;
        }
        if (this.form.home_district_id && this.form.home_prov_id) {
          const dist = await api.get(`/getItemsCondition/district/district_name_lao/district_id/prov_id/${this.form.home_prov_id}`);
          const found = (dist.data || []).find(i => i.value == this.form.home_district_id);
          if (found) this.locationNames.home_district = found.text;
        }
      } catch (e) {
        console.error('populateLocationNames error:', e);
      }
    },
    async openLocationModal(type) {
      if (type === 'district' && !this.form.home_prov_id) {
        this.showResult('error', 'ແຈ້ງເຕືອນ', 'ກະລຸນາເລືອກແຂວງກ່ອນ');
        return;
      }
      this.locationModal.show = true;
      this.locationModal.type = type;
      this.locationModal.loading = true;
      this.locationModal.searchQuery = '';
      this.locationModal.title = type === 'province' ? 'ເລືອກແຂວງ' : 'ເລືອກເມືອງ';
      this.disableScroll();
      try {
        const url = type === 'province'
          ? '/getItems/province/prov_name/prov_id'
          : `/getItemsCondition/district/district_name_lao/district_id/prov_id/${this.form.home_prov_id}`;
        const res = await api.get(url);
        this.locationModal.items = (res.data || []).map(i => ({ id: i.value, name: i.text }));
      } catch (e) {
        console.error('openLocationModal error:', e);
      } finally {
        this.locationModal.loading = false;
        this.$nextTick(() => this.$refs.locationSearchInput && this.$refs.locationSearchInput.focus());
      }
    },
    selectLocationItem(item) {
      if (this.locationModal.type === 'province') {
        this.form.home_prov_id = item.id;
        this.locationNames.home_province = item.name;
        this.form.home_district_id = null;
        this.locationNames.home_district = '';
      } else {
        this.form.home_district_id = item.id;
        this.locationNames.home_district = item.name;
      }
      this.closeLocationModal();
    },
    closeLocationModal() {
      this.locationModal.show = false;
      this.enableScroll();
    },
    handlePhotoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        this.showResult('error', 'ຮູບໃຫຍ່ເກີນໄປ', 'ຂະໜາດຮູບພາບຕ້ອງບໍ່ເກີນ 2MB');
        event.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => { this.form.current_photo_base64 = e.target.result; };
      reader.readAsDataURL(file);
    },
    removePhoto() {
      this.form.current_photo_base64 = null;
      const fileInput = this.$el.querySelector('.avatar-panel input[type="file"]');
      if (fileInput) fileInput.value = '';
    },
    handleMultiFileUpload(event, field) {
      const files = Array.from(event.target.files || []);
      const maxSize = 5 * 1024 * 1024;
      files.forEach(file => {
        if (file.size > maxSize) {
          this.showResult('error', 'ໄຟລ໌ໃຫຍ່ເກີນໄປ', `${file.name} ຂະໜາດເກີນ 5MB`);
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form[field].push({ name: file.name, data: e.target.result });
        };
        reader.readAsDataURL(file);
      });
      event.target.value = '';
    },
    removeMultiFile(field, index) {
      this.form[field].splice(index, 1);
    },
    validateForm() {
      const newErrors = {};
      if (!this.form.student_code) newErrors.student_code = 'ກະລຸນາປ້ອນ ຫຼື ສ້າງລະຫັດ';
      if (!this.form.first_name_lao) newErrors.first_name_lao = 'ກະລຸນາປ້ອນຊື່ (ພາສາລາວ)';
      if (!this.form.last_name_lao) newErrors.last_name_lao = 'ກະລຸນາປ້ອນນາມສະກຸນ (ພາສາລາວ)';
      if (!this.form.gender) newErrors.gender = 'ກະລຸນາເລືອກເພດ';
      if (!this.form.date_of_birth) newErrors.date_of_birth = 'ກະລຸນາເລືອກວັນເກີດ';
      if (!this.form.passport_number) newErrors.passport_number = 'ກະລຸນາປ້ອນເລກໜັງສືຜ່ານແດນ';
      if (!this.form.host_country) newErrors.host_country = 'ກະລຸນາປ້ອນປະເທດທີ່ໄປສຶກສາ';
      if (!this.form.degree_level) newErrors.degree_level = 'ກະລຸນາເລືອກລະດັບການສຶກສາ';
      if (!this.form.field_of_study) newErrors.field_of_study = 'ກະລຸນາເລືອກສາຂາວິຊາ';
      if (!this.form.funding_type) newErrors.funding_type = 'ກະລຸນາເລືອກປະເພດທຶນການສຶກສາ';

      // ສົກຮຽນເລີ່ມຕົ້ນ ຕ້ອງແມ່ນຮູບແບບ YYYY-YYYY ແລະ ປີທີສອງຕ້ອງຫຼັງປີທຳອິດ 1 ປີ (ເຊັ່ນ: 2026-2027)
      if (!this.form.academic_year_start) {
        newErrors.academic_year_start = 'ກະລຸນາປ້ອນສົກຮຽນເລີ່ມຕົ້ນ';
      } else {
        const m = /^(\d{4})-(\d{4})$/.exec(this.form.academic_year_start.trim());
        if (!m || Number(m[2]) !== Number(m[1]) + 1) {
          newErrors.academic_year_start = 'ຮູບແບບບໍ່ຖືກຕ້ອງ ຕ້ອງເປັນ YYYY-YYYY ເຊັ່ນ: 2026-2027';
        }
      }

      // ປີຄາດວ່າຈະຈົບ / ປີທີ່ຈົບ ຕ້ອງແມ່ນຮູບແບບ YYYY (4 ໂຕເລກ)
      if (!this.form.expected_graduation) {
        newErrors.expected_graduation = 'ກະລຸນາປ້ອນປີຄາດວ່າຈະຈົບການສຶກສາ';
      } else if (!/^\d{4}$/.test(this.form.expected_graduation.trim())) {
        newErrors.expected_graduation = 'ຮູບແບບບໍ່ຖືກຕ້ອງ ຕ້ອງເປັນ YYYY ເຊັ່ນ: 2030';
      }

      this.errors = newErrors;
      return Object.keys(newErrors).length === 0;
    },
    showResult(type, title, message) {
      this.resultModal = { show: true, type, title, message };
    },
    closeResultModal() {
      this.resultModal.show = false;
      if (this.resultModal.type === 'success') {
        this.$router.push('/home/students/oversea/list');
      }
    },
    async handleSubmit() {
      if (!this.validateForm()) return;
      this.submitting = true;
      try {
        let res;
        const payload = { ...this.form, full_name_lao: this.fullNameLao, full_name_eng: this.fullNameEng };
        if (this.isEdit) {
          res = await api.put(`/update/oversea_students/${this.$route.params.id}`, { items: payload });
        } else {
          res = await api.post('/insertOne', {
            items: {
              ...payload,
              created_by: this.user.username || this.user.fullname || '',
              created_at: new Date().toISOString()
            },
            table: 'oversea_students'
          });
        }
        if (res.data.code === 1) {
          this.showResult('success', 'ສຳເລັດແລ້ວ', 'ບັນທຶກຂໍ້ມູນນັກສຶກສາຮຽບຮ້ອຍແລ້ວ');
        } else {
          this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', res.data.desc || 'ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້');
        }
      } catch (e) {
        console.error('Submit error:', e);
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີໄດ້');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.staff-form-container { animation: fadeIn 0.4s ease; }
.form-layout { display: grid; grid-template-columns: 300px 1fr; gap: 24px; margin-top: 10px; align-items: start; }

.avatar-panel { padding: 30px 20px; text-align: center; }
.avatar-wrapper { margin-bottom: 20px; }
.avatar-view { width: 160px; height: 160px; border-radius: 50%; border: 4px solid var(--lao-light); margin: 0 auto 15px; position: relative; background: #f8fafc; overflow: hidden; }
.avatar-view img { width: 100%; height: 100%; object-fit: cover; }
.avatar-view img.is-empty { padding: 35px; opacity: 0.2; }
.remove-avatar { position: absolute; top: 5px; right: 5px; background: var(--danger); color: white; border: none; border-radius: 50%; width: 26px; height: 26px; cursor: pointer; font-weight: bold; }
.upload-label { background: var(--gold); color: white; padding: 8px 20px; border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer; transition: 0.2s; display: inline-block; }
.upload-label:hover { background: #b08520; transform: translateY(-1px); }
.file-upload-label { margin-bottom: 14px; }

.id-section { border-top: 1px solid var(--border); padding-top: 20px; text-align: left; }
.id-input-group { display: flex; align-items: center; background: #f1f5f9; border-radius: 8px; overflow: hidden; border: 1px solid var(--border); }
.id-input-group input { flex: 1; background: transparent; border: none !important; font-weight: 700; color: var(--lao); font-family: 'IBM Plex Mono', monospace; padding-left: 15px; }
.refresh-id { background: var(--lao-light); border: none; color: var(--lao); padding: 10px 15px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 13px; transition: 0.2s; white-space: nowrap; }
.refresh-id:hover { background: var(--lao); color: white; }

.input-with-action { position: relative; display: flex; align-items: center; }
.input-with-action input { padding-right: 40px !important; cursor: pointer; background: white !important; }
.btn-icon { position: absolute; right: 12px; background: none; border: none; color: var(--lao); cursor: pointer; display: flex; align-items: center; font-size: 14px; }
.input-with-action.disabled { opacity: 0.6; pointer-events: none; background: #f8fafc !important; }
.pointer { cursor: pointer; }

.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.field-row-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.field-row-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
.form-group { margin-bottom: 20px; }
.form-group label { display: block; font-size: 13px; color: var(--text2); margin-bottom: 8px; font-weight: 600; }
.req { color: var(--danger); }
.form-group input, .form-group select, .form-group textarea { width: 100%; padding: 10px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; transition: all 0.2s; }
.form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: var(--lao); box-shadow: 0 0 0 3px var(--lao-light); }
.form-group.has-error input, .form-group.has-error select { border-color: var(--danger); background-color: #fffafa; }
.error-text { color: var(--danger); font-size: 12px; margin-top: 5px; }

.tabs-container { background: #fcfcfc; border-radius: 12px; overflow: hidden; }
.tabs-nav { display: flex; gap: 5px; padding: 10px 15px 0; background: #f1f5f9; flex-wrap: wrap; }
.tab-btn { padding: 10px 20px; border: none; background: none; cursor: pointer; font-size: 13px; font-weight: 600; color: #64748b; border-radius: 8px 8px 0 0; transition: 0.2s; display: flex; align-items: center; gap: 8px; }
.tab-btn i { font-size: 14px; opacity: 0.7; }
.tab-btn:hover { background: rgba(0, 63, 138, 0.05); color: var(--lao); }
.tab-btn.active { background: white; color: var(--lao); box-shadow: 0 -4px 10px -4px rgba(0, 0, 0, 0.05); }
.tab-complete-icon { font-size: 12px !important; color: #10b981; margin-left: 4px; opacity: 1 !important; }
.tab-content { padding: 20px; background: white; min-height: 200px; }
.border-top { border-top: 1px solid var(--border); }
.pt-4 { padding-top: 1.5rem; }
.mt-4 { margin-top: 1.5rem; }
.mb-4 { margin-bottom: 1.5rem; }

.record-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; }
.record-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; border-bottom: 1px dashed #cbd5e1; padding-bottom: 10px; }
.record-header h4 { margin: 0; color: var(--text2); font-size: 14px; }

.file-list { list-style: none; padding: 0; margin: 0; }
.file-list li { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: white; border: 1px solid var(--border); border-radius: 8px; margin-bottom: 8px; font-size: 13px; }
.btn-remove-file { margin-left: auto; background: var(--danger); color: white; border: none; border-radius: 50%; width: 20px; height: 20px; cursor: pointer; font-weight: bold; line-height: 1; }

.form-actions-wrapper { display: flex; justify-content: flex-end; gap: 12px; padding-top: 20px; border-top: 1px solid var(--border); }
.btn-save { background: var(--lao); color: white; border: none; padding: 12px 30px; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 14px; transition: 0.2s; }
.btn-save:hover { background: var(--lao-mid); box-shadow: 0 4px 12px rgba(0,63,138,0.2); }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-cancel { background: #e2e8f0; color: var(--text2); border: none; padding: 12px 25px; border-radius: 8px; cursor: pointer; font-weight: 600; }

@media (max-width: 900px) {
  .form-layout { grid-template-columns: 1fr; }
  .form-sidebar { position: static; width: 100%; }
  .avatar-view { width: 140px; height: 140px; }
  .field-row, .field-row-3, .field-row-4 { grid-template-columns: 1fr; gap: 10px; }
}

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* Modal Styling */
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 9999; padding: 20px; }
.custom-modal-card { background: white; width: 100%; max-width: 480px; border-radius: 16px; display: flex; flex-direction: column; max-height: 80vh; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); animation: modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.result-modal { padding: 40px 30px; max-width: 400px !important; text-align: center; }
.custom-modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
.custom-modal-header h3 { margin: 0; font-size: 1.1rem; font-weight: 700; color: var(--lao); }
.close-btn { background: #f1f5f9; border: none; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; color: #64748b; font-size: 20px; display: flex; align-items: center; justify-content: center; }
.custom-modal-search { padding: 16px 24px; background: #f8fafc; border-bottom: 1px solid #f1f5f9; }
.custom-modal-search .input-container { position: relative; }
.custom-modal-search .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.custom-modal-search input { width: 100%; padding: 10px 10px 10px 40px; border: 1px solid #e2e8f0; border-radius: 10px; outline: none; transition: 0.2s; }
.custom-modal-body { flex: 1; overflow-y: auto; padding: 8px 0; }
.list-item { padding: 14px 24px; display: flex; justify-content: space-between; align-items: center; cursor: pointer; transition: 0.2s; }
.list-item:hover { background: #f0f7ff; }
.list-item .item-name { font-size: 15px; color: #334155; font-weight: 500; }
.list-item i { color: #cbd5e1; font-size: 12px; }
.custom-modal-footer { padding: 16px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; }
.btn-modal-cancel { background: white; border: 1px solid #e2e8f0; padding: 8px 20px; border-radius: 8px; font-weight: 600; color: #64748b; cursor: pointer; transition: 0.2s; }
.btn-modal-cancel:hover { background: #f8fafc; color: #0f172a; }
.loading-state, .empty-state { padding: 40px; text-align: center; color: #94a3b8; }
.modal-icon { font-size: 64px; margin-bottom: 20px; }
.modal-icon.success { color: #10b981; }
.modal-icon.error { color: #ef4444; }
.modal-content-area h2 { margin: 0 0 10px; font-size: 1.6rem; color: #0f172a; font-weight: 800; }
.modal-content-area p { color: #334155; font-size: 1.05rem; line-height: 1.6; margin-bottom: 30px; font-weight: 500; }
.modal-footer-actions { display: flex; gap: 12px; justify-content: center; }
.btn-modal-primary { padding: 12px 24px; border: none; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 14px; background: var(--lao); color: white; transition: 0.2s; min-width: 140px; }
.btn-modal-primary.success { background: #10b981; }
.btn-modal-primary.error { background: #ef4444; }
.modal-enter-active, .modal-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter-active .custom-modal-card { animation: modalIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.modal-leave-active .custom-modal-card { animation: modalOut 0.2s ease-in; }
@keyframes modalIn { from { opacity: 0; transform: scale(0.8) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes modalOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.9); } }
@keyframes modalPop { from { opacity: 0; transform: scale(0.95) translateY(-10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
</style>
